/**
 * Open a clean printable window with a productivity report.
 * Supports both single-mechanic detail and combined workshop view.
 */
const fmtCurrency = (v) => `$${(Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function escape(s) {
  return String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function statusBadge(status) {
  const map = {
    exceeding: { label: 'Exceeding', color: '#047857', bg: '#d1fae5' },
    on_target: { label: 'On Target', color: '#92400e', bg: '#fef3c7' },
    underperforming: { label: 'Below Target', color: '#991b1b', bg: '#fee2e2' },
  };
  const s = map[status] || { label: status, color: '#334155', bg: '#e2e8f0' };
  return `<span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:600;color:${s.color};background:${s.bg};text-transform:uppercase;letter-spacing:0.05em;">${s.label}</span>`;
}

function buildWorkshopReport({ data, monthName, year, workshopName, expectedDaily = 7.6 }) {
  const rows = (data.per_mechanic || []).map((m, i) => `
    <tr>
      <td>${i + 1}</td>
      <td><strong>${escape(m.mechanic_name)}</strong></td>
      <td style="text-align:right;">${m.expected_hours.toFixed(1)}h</td>
      <td style="text-align:right;">${m.actual_hours.toFixed(1)}h</td>
      <td style="text-align:right;color:${m.deficit_hours > 0 ? '#dc2626' : '#059669'};">
        ${m.deficit_hours > 0 ? '−' : '+'}${Math.abs(m.deficit_hours).toFixed(1)}h
      </td>
      <td style="text-align:right;font-weight:600;">${m.performance_pct.toFixed(0)}%</td>
      <td style="text-align:right;">${m.days_worked}</td>
      <td>${statusBadge(m.status)}</td>
      <td class="signature-cell"></td>
    </tr>
  `).join('');

  return `
    <div class="header">
      <h1>${escape(workshopName)} — Workshop Productivity Report</h1>
      <p class="subtitle">${monthName} ${year} · ${data.working_days} working days · target ${expectedDaily}h/mechanic/day</p>
    </div>
    <div class="kpis">
      <div class="kpi"><span class="kpi-label">Total Mechanics</span><span class="kpi-value">${data.total_mechanics}</span></div>
      <div class="kpi"><span class="kpi-label">Expected</span><span class="kpi-value">${data.total_expected_hours.toFixed(0)}h</span></div>
      <div class="kpi"><span class="kpi-label">Actual</span><span class="kpi-value">${data.total_actual_hours.toFixed(0)}h</span></div>
      <div class="kpi"><span class="kpi-label">Deficit</span><span class="kpi-value" style="color:${data.total_deficit_hours > 0 ? '#dc2626' : '#059669'};">${data.total_deficit_hours > 0 ? '−' : '+'}${Math.abs(data.total_deficit_hours).toFixed(0)}h</span></div>
      <div class="kpi"><span class="kpi-label">Performance</span><span class="kpi-value">${data.overall_performance_pct.toFixed(0)}%</span></div>
      <div class="kpi"><span class="kpi-label">Est. Revenue Loss</span><span class="kpi-value" style="color:#dc2626;">${fmtCurrency(data.estimated_revenue_loss)}</span></div>
    </div>
    <table class="data-table">
      <thead>
        <tr>
          <th style="width:32px;">#</th>
          <th>Mechanic</th>
          <th style="text-align:right;">Expected</th>
          <th style="text-align:right;">Actual</th>
          <th style="text-align:right;">Deficit</th>
          <th style="text-align:right;">Perf %</th>
          <th style="text-align:right;">Days</th>
          <th>Status</th>
          <th style="width:160px;">Manager Signature</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
      <tfoot>
        <tr>
          <td colspan="2"><strong>WORKSHOP TOTAL</strong></td>
          <td style="text-align:right;"><strong>${data.total_expected_hours.toFixed(1)}h</strong></td>
          <td style="text-align:right;"><strong>${data.total_actual_hours.toFixed(1)}h</strong></td>
          <td style="text-align:right;color:${data.total_deficit_hours > 0 ? '#dc2626' : '#059669'};"><strong>${data.total_deficit_hours > 0 ? '−' : '+'}${Math.abs(data.total_deficit_hours).toFixed(1)}h</strong></td>
          <td style="text-align:right;"><strong>${data.overall_performance_pct.toFixed(0)}%</strong></td>
          <td colspan="3"></td>
        </tr>
      </tfoot>
    </table>
    <div class="footer">
      <div class="sig-block">
        <div class="sig-line"></div>
        <p>Service Manager — Signature & Date</p>
      </div>
      <div class="sig-block">
        <div class="sig-line"></div>
        <p>Reviewed by — Signature & Date</p>
      </div>
    </div>
  `;
}

function buildMechanicReport({ mechanic, monthName, year, workshopName, expectedDaily = 7.6 }) {
  const daysInMonth = new Date(year, parseInt(monthName) || 1, 0).getDate ? null : null; // fall through
  // Use fromIso fallback
  // Build daily rows
  const monthIdx = mechanic._monthIdx || 1;
  const yr = mechanic._year || year;
  const dim = new Date(yr, monthIdx, 0).getDate();
  const daily = mechanic.daily_hours || {};
  let totalHrs = 0;
  let workedDays = 0;
  const rows = [];
  for (let d = 1; d <= dim; d++) {
    const iso = `${yr}-${String(monthIdx).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const cell = daily[iso];
    const hrs = cell ? cell.hours : 0;
    const src = cell ? cell.source : '—';
    const wkday = new Date(yr, monthIdx - 1, d).getDay();
    const isWeekend = wkday === 0 || wkday === 6;
    const expected = isWeekend ? 0 : expectedDaily;
    const variance = hrs - expected;
    if (hrs > 0) { totalHrs += hrs; workedDays += 1; }
    const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][wkday];
    rows.push(`
      <tr style="${isWeekend ? 'background:#f8fafc;color:#94a3b8;' : ''}">
        <td>${d}</td>
        <td>${dayName}</td>
        <td style="text-align:right;">${expected ? expected.toFixed(1) + 'h' : '—'}</td>
        <td style="text-align:right;font-weight:${hrs > 0 ? '600' : '400'};">${hrs > 0 ? hrs.toFixed(1) + 'h' : '—'}</td>
        <td style="text-align:right;color:${variance > 0 ? '#059669' : variance < 0 && !isWeekend ? '#dc2626' : '#64748b'};">
          ${hrs > 0 || !isWeekend ? (variance > 0 ? '+' : '') + variance.toFixed(1) + 'h' : '—'}
        </td>
        <td style="font-size:10px;color:#94a3b8;">${src === 'manual' ? 'Manual' : src === 'auto' ? 'Auto' : '—'}</td>
        <td class="signature-cell"></td>
      </tr>
    `);
  }

  return `
    <div class="header">
      <h1>${escape(workshopName)} — Mechanic Productivity Report</h1>
      <p class="subtitle">${escape(mechanic.mechanic_name)} · ${monthName} ${year}</p>
    </div>
    <div class="kpis">
      <div class="kpi"><span class="kpi-label">Expected</span><span class="kpi-value">${mechanic.expected_hours.toFixed(0)}h</span></div>
      <div class="kpi"><span class="kpi-label">Actual</span><span class="kpi-value">${mechanic.actual_hours.toFixed(0)}h</span></div>
      <div class="kpi"><span class="kpi-label">Deficit</span><span class="kpi-value" style="color:${mechanic.deficit_hours > 0 ? '#dc2626' : '#059669'};">${mechanic.deficit_hours > 0 ? '−' : '+'}${Math.abs(mechanic.deficit_hours).toFixed(0)}h</span></div>
      <div class="kpi"><span class="kpi-label">Performance</span><span class="kpi-value">${mechanic.performance_pct.toFixed(0)}%</span></div>
      <div class="kpi"><span class="kpi-label">Days Worked</span><span class="kpi-value">${mechanic.days_worked}</span></div>
      <div class="kpi"><span class="kpi-label">Status</span><span class="kpi-value" style="font-size:11px;">${statusBadge(mechanic.status)}</span></div>
    </div>
    <table class="data-table">
      <thead>
        <tr>
          <th style="width:36px;">Day</th>
          <th style="width:42px;">Wk</th>
          <th style="text-align:right;">Expected</th>
          <th style="text-align:right;">Actual</th>
          <th style="text-align:right;">Variance</th>
          <th>Source</th>
          <th style="width:160px;">Notes / Sign-off</th>
        </tr>
      </thead>
      <tbody>${rows.join('')}</tbody>
      <tfoot>
        <tr>
          <td colspan="2"><strong>TOTAL</strong></td>
          <td style="text-align:right;"><strong>${mechanic.expected_hours.toFixed(1)}h</strong></td>
          <td style="text-align:right;"><strong>${totalHrs.toFixed(1)}h</strong></td>
          <td style="text-align:right;"><strong style="color:${(totalHrs - mechanic.expected_hours) >= 0 ? '#059669' : '#dc2626'};">${(totalHrs - mechanic.expected_hours) >= 0 ? '+' : ''}${(totalHrs - mechanic.expected_hours).toFixed(1)}h</strong></td>
          <td colspan="2"><strong>${workedDays} days worked</strong></td>
        </tr>
      </tfoot>
    </table>
    <div class="discussion-box">
      <p class="section-title">Discussion Points / Coaching Notes</p>
      <div class="lined-box"></div>
    </div>
    <div class="footer">
      <div class="sig-block">
        <div class="sig-line"></div>
        <p>Mechanic — ${escape(mechanic.mechanic_name)}</p>
      </div>
      <div class="sig-block">
        <div class="sig-line"></div>
        <p>Manager — Signature & Date</p>
      </div>
    </div>
  `;
}

const PRINT_CSS = `
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #0f172a; padding: 24px; max-width: 1100px; margin: 0 auto; }
  .header { border-bottom: 3px solid #1e40af; padding-bottom: 12px; margin-bottom: 20px; }
  .header h1 { margin: 0; font-size: 22px; color: #1e3a8a; }
  .subtitle { margin: 4px 0 0; color: #64748b; font-size: 13px; }
  .kpis { display: grid; grid-template-columns: repeat(6, 1fr); gap: 8px; margin-bottom: 20px; }
  .kpi { padding: 10px; background: #f1f5f9; border-radius: 6px; border-left: 3px solid #3b82f6; }
  .kpi-label { display: block; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; }
  .kpi-value { display: block; font-size: 18px; font-weight: 700; color: #0f172a; margin-top: 2px; }
  .data-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 11px; }
  .data-table th { background: #1e40af; color: white; padding: 8px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; }
  .data-table td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; }
  .data-table tbody tr:nth-child(even) { background: #f8fafc; }
  .data-table tfoot { background: #dbeafe; }
  .data-table tfoot td { padding: 8px; border-top: 2px solid #1e40af; font-size: 12px; }
  .signature-cell { border-bottom: 1px dashed #94a3b8; }
  .footer { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; margin-top: 40px; }
  .sig-block { text-align: center; }
  .sig-line { border-top: 1px solid #0f172a; margin-bottom: 6px; padding-top: 30px; }
  .sig-block p { margin: 0; font-size: 11px; color: #64748b; }
  .discussion-box { margin-top: 24px; }
  .section-title { font-size: 12px; font-weight: 600; color: #1e40af; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 6px; }
  .lined-box { border: 1px solid #cbd5e1; border-radius: 6px; height: 120px; background: repeating-linear-gradient(transparent, transparent 23px, #e2e8f0 24px); }
  .print-btn { position: fixed; top: 16px; right: 16px; padding: 8px 16px; background: #2563eb; color: white; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
  .print-btn:hover { background: #1d4ed8; }
  @media print {
    body { padding: 0; }
    .print-btn { display: none; }
    .data-table { page-break-inside: auto; }
    .data-table tr { page-break-inside: avoid; page-break-after: auto; }
    .footer { page-break-inside: avoid; }
  }
`;

export function printProductivityReport({ mode, data, mechanic, month, year, monthName, workshopName = 'AutoShopFlow Workshop', expectedDaily = 7.6 }) {
  const w = window.open('', '_blank', 'width=1100,height=800');
  if (!w) {
    alert('Pop-ups must be allowed to print the report.');
    return;
  }
  // Tag mechanic with month/year for daily breakdown reconstruction
  if (mechanic) {
    mechanic._monthIdx = month;
    mechanic._year = year;
  }
  const body = mode === 'mechanic'
    ? buildMechanicReport({ mechanic, monthName, year, workshopName, expectedDaily })
    : buildWorkshopReport({ data, monthName, year, workshopName, expectedDaily });
  w.document.write(`<!doctype html>
<html><head>
<meta charset="utf-8" />
<title>${mode === 'mechanic' ? `${mechanic.mechanic_name} — ${monthName} ${year}` : `Workshop Productivity — ${monthName} ${year}`}</title>
<style>${PRINT_CSS}</style>
</head><body>
<button class="print-btn" onclick="window.print()">Print / Save PDF</button>
${body}
</body></html>`);
  w.document.close();
  // Auto-open print dialog after layout settles
  setTimeout(() => { try { w.focus(); w.print(); } catch (e) {} }, 500);
}
